import { CalendarX, ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { VoltDataTable, type VoltColumn } from "../ui/VoltDataTable";
import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltPageHeader } from "../ui/VoltPageHeader";
import { withVoltParam } from "../ui/voltFilters";
import {
  useBillableSession,
  useLastSynced,
  useMonthSessions,
} from "./attendanceApi";
import {
  countEntries,
  KIND_LABELS,
  formatDate,
  formatTime,
  goesToBv,
  monthLabel,
  parseMonth,
  shiftMonth,
  syncedAgo,
} from "./attendanceLogic";
import { AttendanceAccess, AttendanceFrame } from "./AttendanceFrame";
import type { AttendanceEntry, AttendanceSession } from "./types";

type Row = AttendanceSession & {
  counts: ReturnType<typeof countEntries>;
  goesToBv: boolean;
};

const columns: VoltColumn<Row>[] = [
  {
    key: "title",
    header: "Sitzung",
    cell: (s) => (
      <span className={s.cancelled ? "line-through" : undefined}>
        {s.title}
      </span>
    ),
  },
  {
    key: "date",
    header: "Datum",
    cell: (s) =>
      `${formatDate(s.starts_at)}, ${formatTime(s.starts_at)}–${formatTime(s.ends_at)}`,
  },
  {
    key: "kind",
    header: "Liste",
    cell: (s) => (
      <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {KIND_LABELS[s.kind]}
      </span>
    ),
  },
  { key: "location", header: "Ort", cell: (s) => s.location ?? "–" },
  {
    key: "present",
    header: "Anwesend",
    align: "right",
    cell: (s) =>
      s.cancelled ? (
        <Badge variant="secondary">abgesagt</Badge>
      ) : (
        `${s.counts.present} / ${s.counts.invited} da`
      ),
  },
  {
    key: "bv",
    header: "BV",
    cell: (s) => (s.goesToBv ? <Badge variant="info">geht an BV</Badge> : null),
  },
];

const MonthSwitch = ({ month }: { month: string }) => {
  const [params, setParams] = useSearchParams();
  const go = (delta: number) =>
    setParams(withVoltParam(params, "month", shiftMonth(month, delta)), {
      replace: true,
    });
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="outline"
        size="icon"
        aria-label="Vormonat"
        onClick={() => go(-1)}
      >
        <ChevronLeft aria-hidden />
      </Button>
      <span className="min-w-36 text-center font-medium" aria-live="polite">
        {monthLabel(month)}
      </span>
      <Button
        variant="outline"
        size="icon"
        aria-label="Nächster Monat"
        onClick={() => go(1)}
      >
        <ChevronRight aria-hidden />
      </Button>
    </div>
  );
};

const SessionList = ({
  district,
}: {
  district: AttendanceSession["district"];
}) => {
  const [params] = useSearchParams();
  const month = parseMonth(params.get("month"));
  const sessions = useMonthSessions(district, month);
  const synced = useLastSynced(district);
  const billable = useBillableSession(district, month);

  const entries = sessions.data?.entries ?? [];
  const bySession = new Map<number, typeof entries>();
  for (const e of entries) {
    bySession.set(e.session_id, [...(bySession.get(e.session_id) ?? []), e]);
  }
  const rows: Row[] = (sessions.data?.sessions ?? []).map((s) => ({
    ...s,
    counts: countEntries(
      (bySession.get(s.id) ?? []) as Pick<
        AttendanceEntry,
        "status" | "invited"
      >[],
    ),
    goesToBv: goesToBv(s, billable.data),
  }));
  const ago = syncedAgo(synced.data ?? null);
  const query = params.toString();

  return (
    <>
      <VoltPageHeader
        title="Anwesenheit"
        description={
          ago
            ? `Sitzungen kommen aus dem Fraktionskalender · zuletzt abgeglichen ${ago}`
            : "Sitzungen kommen aus dem Fraktionskalender · noch nie abgeglichen"
        }
        action={
          <Button variant="outline" asChild>
            <Link to={`/attendance/settings${query ? `?${query}` : ""}`}>
              <Settings aria-hidden />
              Einstellungen
            </Link>
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <MonthSwitch month={month} />
      </div>
      {sessions.error ? (
        <p role="alert" className="text-sm text-destructive">
          Die Sitzungen konnten nicht geladen werden.
        </p>
      ) : (
        <VoltDataTable
          rows={rows}
          columns={columns}
          rowKey={(s) => s.id}
          rowHref={(s) => `/attendance/sessions/${s.id}`}
          loading={sessions.isPending}
          empty={
            <VoltEmpty
              icon={CalendarX}
              title={`Keine Sitzungen im ${monthLabel(month)}`}
              description="Sitzungen erscheinen hier, sobald der Kalenderabgleich sie gefunden hat."
            />
          }
        />
      )}
    </>
  );
};

export const AttendancePage = () => (
  <AttendanceFrame title="Anwesenheit">
    <AttendanceAccess>
      {({ district }) => <SessionList district={district} />}
    </AttendanceAccess>
  </AttendanceFrame>
);

AttendancePage.path = "/attendance";
